import type { IkeQueryWhere } from '@ikegps/core-api';
import type { ParseJSONObject } from '@ikegps/core-api';
import { type IkeActionQuery, IkeQuerySort, type IkeQueryWhere as IkeActionIkeQueryWhere } from '@ikegps/core-api';
import type { Log } from '../log';
import type { IkeRestObject } from './objects/base';
import type { IkeRest } from './rest';

export const MAX_API_RESULTS = 5000;

function getGTLTValue<T>(value: T): T {
	if (typeof value !== 'string') {
		return value;
	}

	if (value.length !== 24) {
		return value;
	}

	const date = new Date(value);
	if (isNaN(date.getTime())) {
		return value;
	}

	return date as any;
}

export class IkeRestQuery<T extends ParseJSONObject> {
	core: IkeRest;
	ike: IkeRestObject<T>;
	departmentId: string;
	sessionToken: string;
	log: Log;

	query: IkeActionQuery<T, '/v1/ike/query/:className'>;

	limitResults: number = 200;
	skippedResults: number = 0;
	where: { [K in keyof T]?: T[K] | IkeQueryWhere<T[K]> } = {};
	sortField: { [K in keyof T]: 1 | -1 } = null;

	skipCache: boolean;

	constructor(core: IkeRest, ike: IkeRestObject<T>, sessionToken: string, departmentId: string, log: Log) {
		this.core = core;
		this.log = log;
		this.ike = ike;
		this.sessionToken = sessionToken;
		this.departmentId = departmentId;
	}

	/**
	 * Sort the query based off the fieldName;
	 *
	 * @param fieldName Sort field name
	 * @param direction 1 = ascending, -1 = descending
	 */
	sort<K extends keyof T>(fieldName: K, direction: IkeQuerySort = IkeQuerySort.ASC): this {
		if (this.sortField == null) {
			this.sortField = {} as any;
		}
		this.sortField[fieldName] = direction;
		return this;
	}

	equal<K extends keyof T>(key: K, value: T[K] | string): this {
		this.where[key] = value as T[K];
		return this;
	}

	in<K extends keyof T>(key: K, value: T[K][] | string[]): this {
		this.where[key] = { $in: value as T[K][] };
		return this;
	}

	gt<K extends keyof T>(key: K, value: T[K]): this {
		const $gt = getGTLTValue(value);

		this.where[key] = { $gt };
		return this;
	}

	lt<K extends keyof T>(key: K, value: T[K]): this {
		const $lt = getGTLTValue(value);

		this.where[key] = { $lt };
		return this;
	}

	limit(limit: number): this {
		this.limitResults = limit;
		if (this.limitResults > MAX_API_RESULTS) {
			throw new Error('QUERY_HUGE_LIMIT');
		}
		return this;
	}

	skip(skip: number): this {
		this.skippedResults = skip;
		return this;
	}

	async find(): Promise<T[]> {
		const startTime = Date.now();
		const queryObj = { where: this.where, limit: this.limitResults, skip: this.skippedResults };

		if (this.sortField) {
			queryObj['order'] = this.sortField;
		}

		const response = await this.core.action<this['query']>(
			`${this.ike.actionName}.query`,
			queryObj,
			this.sessionToken,
			this.departmentId
		);
		const duration = Date.now() - startTime;
		const documents = response.results;

		this.log.info(
			{
				object: this.ike.className,
				found: documents.length,
				keys: Object.keys(this.where).join(', '),
				duration,
			},
			'find'
		);
		return documents;
	}

	async findAll(): Promise<T[]> {
		const startTime = Date.now();

		const LIMIT = MAX_API_RESULTS;
		let queryResLen = LIMIT;
		let queryObj: {
			where: { [K in keyof T]?: T[K] | IkeActionIkeQueryWhere<T[K]> };
			limit: number;
			skip: number;
		};
		let response: this['query']['response'];
		let duration;
		let documents: T[] = [];

		let count = 0;
		while (queryResLen >= LIMIT) {
			queryObj = { where: this.where, limit: LIMIT, skip: LIMIT * count };
			if (this.sortField) {
				queryObj['order'] = this.sortField;
			}
			response = await this.core.action<this['query']>(
				`${this.ike.actionName}.query`,
				queryObj,
				this.sessionToken,
				this.departmentId
			);
			if (documents.length > 0) {
				documents = documents.concat(response.results);
			} else {
				documents = response.results;
			}
			queryResLen = response.results.length;
			count++;
		}
		duration = Date.now() - startTime;

		this.log.info(
			{
				object: this.ike.className,
				found: documents.length,
				keys: Object.keys(this.where).join(', '),
				duration,
			},
			'find'
		);

		return documents;
	}

	async findOne(): Promise<T> {
		this.limitResults = 1;
		const documents = await this.find();
		return documents[0];
	}
}
